import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { Icon } from './Icon.jsx';

function KindBadge({ kind }) {
  const isTicket = kind === 'ticket';
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '2px 5px',
        borderRadius: 4,
        flexShrink: 0,
        background: isTicket ? '#F2EAFE' : '#E3F0FF',
        color: isTicket ? '#5E2A9C' : '#0747A6',
      }}
    >
      {isTicket ? 'TICKET' : 'EPIC'}
    </span>
  );
}

/**
 * Search field for both Jira epics and tickets. Replaces the old EpicSearch
 * which only queried epics. Emits an item shaped:
 *   { kind, id, title, parentEpicId, parentEpicTitle }
 * The parent reads this to split into ticketId/epicId before persisting.
 */
export function JiraSearch({ value, valueTitle, valueKind, onChange }) {
  const initial = value ? `${value}${valueTitle ? ` — ${valueTitle}` : ''}` : '';
  const [q, setQ] = useState(initial);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const runSearch = (query) => {
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.searchJira(query);
        setResults(data.items || []);
        setAvailable(data.available !== false);
      } catch {
        setAvailable(false);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const onFocus = () => {
    setOpen(true);
    runSearch(q);
  };
  const onInput = (e) => {
    const val = e.target.value;
    setQ(val);
    runSearch(val);
    setOpen(true);
    if (!val) {
      onChange(null);
    } else if (!available) {
      onChange({ kind: 'epic', id: val.trim(), title: '', parentEpicId: null, parentEpicTitle: null });
    }
  };
  const select = (item) => {
    setQ(`${item.id} — ${item.title}`);
    onChange(item);
    setOpen(false);
  };
  const clear = () => {
    setQ('');
    onChange(null);
    setOpen(false);
  };

  // Derive selected-state hint from the current value
  const selectedItem = value && valueKind ? { kind: valueKind, id: value, title: valueTitle, parentEpicId: null } : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        className="tt-input"
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px 4px 14px' }}
      >
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name="search" size={15} />
        </span>
        <input
          type="text"
          value={q}
          onChange={onInput}
          onFocus={onFocus}
          placeholder={
            !available
              ? 'PROJ-123 (Jira unavailable — type manually)'
              : 'Search Jira — epic or ticket (e.g. PAY-412 or PAY-2087)'
          }
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            padding: '7px 0',
            background: 'transparent',
            fontSize: 14,
            color: 'var(--sp-fg-1)',
          }}
        />
        {q && (
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            style={{ width: 28, height: 28 }}
            onClick={clear}
            title="Clear"
          >
            <Icon name="x" size={13} />
          </button>
        )}
      </div>

      {!available && (
        <div className="tt-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#C4320A' }}>
          <Icon name="alert" size={12} /> Jira unavailable — enter the key manually
        </div>
      )}

      {open && available && (
        <div className="tt-pop" style={{ top: 'calc(100% + 4px)', left: 0, right: 0 }}>
          {loading && (
            <div className="tt-pop-item" style={{ color: 'var(--sp-fg-3)' }}>
              Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="tt-pop-item" style={{ color: 'var(--sp-fg-3)' }}>
              No matching epics or tickets
            </div>
          )}
          {!loading &&
            results.map((item) => (
              <div
                key={item.id}
                className="tt-pop-item"
                onClick={() => select(item)}
                style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                  <KindBadge kind={item.kind} />
                  <span style={{ fontWeight: 700, fontFamily: 'DM Sans', color: '#0052CC', fontVariantNumeric: 'tabular-nums' }}>
                    {item.id}
                  </span>
                  <span
                    style={{ color: 'var(--sp-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                  >
                    {item.title}
                  </span>
                </div>
                {item.kind === 'ticket' && (
                  <div style={{ fontSize: 11, color: item.parentEpicId ? 'var(--sp-fg-3)' : 'var(--sp-fg-4)', paddingLeft: 4 }}>
                    {item.parentEpicId ? (
                      <>↳ in epic <strong style={{ color: '#0052CC' }}>{item.parentEpicId}</strong> — {item.parentEpicTitle}</>
                    ) : (
                      'Standalone — no parent epic'
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * Renders the selected-state hint beneath the JiraSearch input.
 * item: { kind, id, title, parentEpicId, parentEpicTitle }
 */
export function JiraSearchHint({ item }) {
  if (!item) return null;
  if (item.kind === 'epic') return null;

  if (item.parentEpicId) {
    return (
      <div className="tt-help" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--sp-fg-2)' }}>
        <span>↪ In epic</span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '1px 6px',
            background: 'var(--sp-bg-muted)',
            borderRadius: 6,
            fontFamily: 'DM Sans',
            fontWeight: 700,
            color: '#0052CC',
            fontSize: 12,
          }}
        >
          {item.parentEpicId}
        </span>
        <span>— logged for both roll-ups.</span>
      </div>
    );
  }

  return (
    <div className="tt-help" style={{ color: 'var(--sp-fg-3)' }}>
      ⓘ Standalone ticket — no parent epic.
    </div>
  );
}
